import { useEffect } from "react"
import Icon from "~/components/ui/Icon"
import { prefetchUrl } from "~/components/ui/PrefetchAnchor"
import type { ProductListingPage } from "@decocms/apps-commerce/types"
import stripTrackingParams from "~/sdk/stripTrackingParams"
import { SITE } from "~/constants"

interface Props {
    pageInfo?: ProductListingPage['pageInfo']
    url: string
}

export const MAX_VISIBLE_PAGES = 5
// https://developers.vtex.com/docs/api-reference/search-api?endpoint=get-/api/catalog_system/pub/products/search
// see _from param
export const MAX_FROM_PARAM_VTEX = 2500
export const VTEX_MAX_PAGE = 50

export default function Pagination({ pageInfo, url: rawUrl }: Props) {
    const { records = 0, recordPerPage = 1, currentPage = 0, previousPage, nextPage } = pageInfo ??
        {}

    const pages = Math.ceil(records / (recordPerPage ?? records)) || 0
    const offset = Math.floor(MAX_VISIBLE_PAGES / 2)
    const missingLeft = Math.max(0, offset - (currentPage - 1))
    const missingRight = Math.max(0, offset - (pages - currentPage))

    // Always emit relative href (path + search) — `.href` includes the
    // hard-coded https://www.miess.com.br base which would send users to
    // production when clicking pagination.
    const rel = (u: URL) => u.pathname + u.search

    // Tracking/PII params (utm_*, gclid, ?email=…) must not be replayed in
    // pagination hrefs (DECO-5819).
    const baseUrl = stripTrackingParams(new URL(rawUrl ?? "/", SITE.URL))

    const previousUrl = new URL(baseUrl)
    const nextUrl = new URL(baseUrl)
    const firstUrl = new URL(baseUrl)
    const lastUrl = new URL(baseUrl)
    const STORE_MAX_PAGE = pages <= VTEX_MAX_PAGE ? pages : VTEX_MAX_PAGE

    if (previousPage) {
        if (currentPage > 2) {
            previousUrl.searchParams.set('page', (currentPage - 1).toString())
        } else {
            previousUrl.searchParams.delete('page')
        }
    }

    if (nextPage && currentPage <= STORE_MAX_PAGE) {
        nextUrl.searchParams.set('page', (currentPage + 1).toString())
        lastUrl.searchParams.set('page', STORE_MAX_PAGE.toString())
    }

    if (previousPage) {
        firstUrl.searchParams.delete('page')
    }

    const nextHref = nextPage && currentPage < STORE_MAX_PAGE ? rel(nextUrl) : null

    // O cache do worker é por URL e compartilhado por segmento: um MISS em
    // ?page=N custa 3-12s de TTFB (waterfall VTEX sem cache de loader). No
    // mobile não existe hover, então além do prefetch por intenção dos links
    // abaixo, aquecemos a próxima página em background depois que a atual
    // assentou — o clique em "Próximo" vira cache HIT (~0,5s).
    useEffect(() => {
        if (!nextHref) return
        const timer = setTimeout(() => prefetchUrl(nextHref), 2000)
        return () => clearTimeout(timer)
    }, [nextHref])

    return (
        <>
            {/* rel=prev/next são emitidos uma única vez no <head> pelo PageSEO (seoHead.ts) */}
            <div data-prerender className='flex justify-center'>
                <div className='join gap-1'>
                    {previousPage && (
                        <>
                            <a
                                aria-label='Ir para primeira página'
                                href={rel(firstUrl)}
                                className='btn btn-ghost no-animation font-medium text-sm join-item btn-xs text-red-wine'
                            >
                                <Icon id='PreviousPage' size={14} />
                            </a>
                            <a
                                rel='prev'
                                href={rel(previousUrl)}
                                className='btn btn-ghost no-animation font-medium text-sm join-item btn-xs text-red-wine normal-case'
                            >
                                Anterior
                            </a>
                        </>
                    )}
                    {new Array(pages).fill(null).filter((_, index) => index < STORE_MAX_PAGE).map(
                        (_, index) => {
                            const page = index + 1
                            const url = new URL(baseUrl)

                            if (
                                page > pages ||
                                page > offset + currentPage + missingLeft ||
                                page < currentPage - offset - missingRight
                            ) {
                                return null
                            }

                            if (page > 1) {
                                url.searchParams.set('page', `${page}`)
                            } else {
                                url.searchParams.delete('page')
                            }

                            return (
                                <a
                                    key={page}
                                    aria-label={`Ir para a página ${page}`}
                                    href={rel(url)}
                                    className='btn btn-ghost no-animation btn-xs hover:text-red-800'
                                    style={page === currentPage
                                        ? { background: "#91153a", border: "1px solid #91153a", borderRadius: "5px", color: "#fff" }
                                        : undefined}
                                >
                                    {page}
                                </a>
                            )
                        },
                    )}
                    {nextPage && currentPage < STORE_MAX_PAGE && (
                        <a
                            rel='next'
                            href={rel(nextUrl)}
                            className='btn btn-ghost join-item btn-xs font-medium text-sm no-animation text-red-wine normal-case'
                        >
                            Próximo
                        </a>
                    )}

                    {nextPage && currentPage < STORE_MAX_PAGE && (
                        <a
                            aria-label='Ir para última página'
                            href={rel(lastUrl)}
                            className='btn btn-ghost join-item font-medium text-sm btn-xs no-animation text-red-wine'
                        >
                            <Icon id='NextPage' size={14} />
                        </a>
                    )}
                </div>
            </div>
        </>
    )
}
